<template>
  <section class="px-14 pt-12 pb-10 mt-3 space-y-8 sm-max:px-5 bg-[#F5F5F5] md-max:pt-10 md-max:pb-6">
    <h3 class=" leading-tight text-center font-ww-medium md-max:px-4 lg:leading-head">
      <strong v-html="title1Row"
        :class="newStyle ? 'text-45 leading-none' : 'lg:text-45 md:text-3xl text-2xl sm-max:text-2xl'"></strong>
    </h3>

    <div>
      <div class="">
        <div v-for="(item, index) in items" :key="index"
          class="grid gap-y-4 items-start mt-6 mb-12 md:grid-cols-12 md-max:mb-10 sm-max:gap-3 lg:gap-y-0">
          <div class="col-span-4 md-max:col-span-12">
            <nuxt-img class="m-auto lg:w-full" sizes="xs:337px sm:440px md:745vw lg:337px xl:337px" width="337"
              height="248" loading="lazy" format="webp" :alt="item.alt" :src="item.imageSrc"></nuxt-img>
          </div>

          <div class="flex flex-col col-span-8 justify-center h-full md-max:col-span-12 md-max:text-center lg:pl-10">
            <h3 class="font-bold font-ww-bold"
              :class="newStyle ? 'text-3xl md-max:text-left md-max:px-4' : 'text-3xl sm-max:text-lg'"
              v-html="item.title"></h3>

            <div class="mt-4 font-ww-regular md:!leading-30 sm-max:mt-2 xl:pr-12" v-html="item.description"
              :class="newStyle ? 'text-xl md-max:text-left md-max:px-4' : 'md:text-lg text-base xl:text-xl md-max:text-center'">
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  title1Row: {
    type: String,
    default: "And that’s not all. This coffee includes <br class=\"sm-max:hidden\">all the above qualities and more:"
  },
  items: {
    type: Array,
    default: () => [
      {
        alt: 'HIGH ELEVATION GROWN',
        imageSrc: '/assets/images/freebag-include-img1.png',
        title: 'High Elevation Grown',
        description:
          'High-altitude specialty coffees, generally command a far better market price due to their exceptional flavor and vibrancy, lower yield per coffee tree, and challenge to coffee farmers in remote mountainous areas. This means truly superb coffee beans that represent one of the world\'s most affordable luxuries.',
      },
      {
        alt: 'Sun-Dried',
        imageSrc: '/assets/images/freebag-include-img2.png',
        title: 'Sun-Dried',
        description:
          'Once washed, the beans are sun-dried until they\'ve achieved a moisture content of 11.5%. (Too much moisture and you risk mold; too dry and the flavor of the coffee is spoiled.) Finally, the beans transition to a 30-day "rest" period which allows the flavor profile of your beans to deepen in richness, complexity and taste.',
      },
      {
        alt: 'Fairly Traded',
        imageSrc: '/assets/images/freebag-include-img10.jpg',
        title: 'Fairly Traded',
        description:
          'This coffee is grown in a sustainable environment. Farmers grow their coffee which is an environmentally friendly way of producing crops. Not only is this good for the environment, but it is better for the coffee. By employing farming methods, the fairly traded coffee is grown by farmers who are paid a fair wage for doing so.',
      },
      {
        alt: 'Speciality Bean',
        imageSrc: '/assets/images/freebag-include-img4.jpg',
        title: 'Speciality Bean',
        description:
          'Specialty coffee represents the very best of the world\'s coffee yield or the TOP 3% of global production. These beans are the largest beans that are hand picked out because they are perfect with no defects. Only the very best.',
      },
      {
        alt: 'Rainforest Alliance Certified (Light, Medium, Dark Roast)',
        imageSrc: '/assets/images/freebag-include-img5.jpg',
        title: 'Rainforest Alliance Certified (Light, Medium, Dark Roast)',
        description: `Some other coffee farms slash and burn the rainforest and spray their coffee beans with toxic pesticides to keep the bugs away. We think it's better to grow our coffee in nutrient-rich virgin soil.
          <br><br>
          If you're anything like me, I prefer to grow our coffee the natural way, in the cool shade of ancient trees, where the beans can ripen slowly and migratory birds, like the beautiful Scarlet Tanager, can naturally manage bugs like the coffee farmer's archenemy - the coffee borer.`,
      },
      {
        alt: 'Tested For Pesticides',
        imageSrc: '/assets/images/freebag-include-img6.jpg',
        title: 'Tested For Pesticides',
        description: `Conventional coffee farms soak the ground with harsh chemical fertilizers - as much as 250 pounds per acre! When you grind up beans that have chemical residue and add hot water to them, you concentrate those chemicals into your cup of coffee.`,
      },
      {
        alt: 'Non-GMO',
        imageSrc: '/assets/images/freebag-include-img7.jpg',
        title: 'Non-GMO',
        description: `Our beans are grown from single origin plants that have not been genetically modified. We believe in keeping our coffee as clean and pure as possible not just for health reasons, but to protect the environment and the delicate flavor profile that you get with every perfect cup.`,
      },
    ]
  },
  newStyle: {
    type: Boolean,
    default: false
  }
})

defineBuilderComponent({
  friendlyName: "TitleBlockImageText",
  inputs: [
    {
      name: "title1Row",
      type: "richText",
      friendlyName: "Main Title",
      defaultValue: "And that’s not all. This coffee includes <br class=\"sm-max:hidden\">all the above qualities and more:"
    },
    {
      name: "items",
      type: "list",
      friendlyName: "Feature Items",
      subFields: [
        {
          name: "alt",
          type: "text",
          friendlyName: "Image Alt Text",
          defaultValue: "Feature Image"
        },
        {
          name: "imageSrc",
          type: "file",
          friendlyName: "Image",
          allowedFileTypes: [
            "jpeg",
            "jpg",
            "png",
            "svg",
            "webp"
          ],
          defaultValue: "/assets/images/default.jpg"
        },
        {
          name: "title",
          type: "richText",
          friendlyName: "Item Title",
          defaultValue: "Feature Title"
        },
        {
          name: "description",
          type: "richText",
          friendlyName: "Description",
          defaultValue: "Feature description goes here."
        }
      ],
      defaultValue: [
        {
          alt: 'HIGH ELEVATION GROWN',
          imageSrc: '/assets/images/freebag-include-img1.png',
          title: 'High Elevation Grown',
          description:
            'High-altitude specialty coffees, generally command a far better market price due to their exceptional flavor and vibrancy, lower yield per coffee tree, and challenge to coffee farmers in remote mountainous areas. This means truly superb coffee beans that represent one of the world\'s most affordable luxuries.',
        },
        {
          alt: 'Sun-Dried',
          imageSrc: '/assets/images/freebag-include-img2.png',
          title: 'Sun-Dried',
          description:
            'Once washed, the beans are sun-dried until they\'ve achieved a moisture content of 11.5%. (Too much moisture and you risk mold; too dry and the flavor of the coffee is spoiled.) Finally, the beans transition to a 30-day "rest" period which allows the flavor profile of your beans to deepen in richness, complexity and taste.',
        },
        {
          alt: 'Fairly Traded',
          imageSrc: '/assets/images/freebag-include-img10.jpg',
          title: 'Fairly Traded',
          description:
            'This coffee is grown in a sustainable environment. Farmers grow their coffee which is an environmentally friendly way of producing crops. Not only is this good for the environment, but it is better for the coffee. By employing farming methods, the fairly traded coffee is grown by farmers who are paid a fair wage for doing so.',
        },
        {
          alt: 'Speciality Bean',
          imageSrc: '/assets/images/freebag-include-img4.jpg',
          title: 'Speciality Bean',
          description:
            'Specialty coffee represents the very best of the world\'s coffee yield or the TOP 3% of global production. These beans are the largest beans that are hand picked out because they are perfect with no defects. Only the very best.',
        },
        {
          alt: 'Rainforest Alliance Certified (Light, Medium, Dark Roast)',
          imageSrc: '/assets/images/freebag-include-img5.jpg',
          title: 'Rainforest Alliance Certified (Light, Medium, Dark Roast)',
          description: `Some other coffee farms slash and burn the rainforest and spray their coffee beans with toxic pesticides to keep the bugs away. We think it's better to grow our coffee in nutrient-rich virgin soil.
          <br><br>
          If you're anything like me, I prefer to grow our coffee the natural way, in the cool shade of ancient trees, where the beans can ripen slowly and migratory birds, like the beautiful Scarlet Tanager, can naturally manage bugs like the coffee farmer's archenemy - the coffee borer.`,
        },
        {
          alt: 'Tested For Pesticides',
          imageSrc: '/assets/images/freebag-include-img6.jpg',
          title: 'Tested For Pesticides',
          description: `Conventional coffee farms soak the ground with harsh chemical fertilizers - as much as 250 pounds per acre! When you grind up beans that have chemical residue and add hot water to them, you concentrate those chemicals into your cup of coffee.`,
        },
        {
          alt: 'Non-GMO',
          imageSrc: '/assets/images/freebag-include-img7.jpg',
          title: 'Non-GMO',
          description: `Our beans are grown from single origin plants that have not been genetically modified. We believe in keeping our coffee as clean and pure as possible not just for health reasons, but to protect the environment and the delicate flavor profile that you get with every perfect cup.`,
        },
      ]
    },
    {
      name: "newStyle",
      type: "boolean",
      defaultValue: false
    }
  ]
});
</script>